import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";
import axios from "axios";
import "../styles/busyOverlay.css";

const DEFAULT_BUSY_MESSAGE = "Please wait...";
const MARKET_ANALYSIS_BUSY_MESSAGE =
  "Fetching live online market prices. This may take a few seconds.";

interface BusyContextType {
  isBusy: boolean;
  busyMessage: string;
  startBusy: (message?: string) => void;
  stopBusy: () => void;
  runBusy: <T>(task: () => Promise<T>, message?: string) => Promise<T>;
}

const BusyContext = createContext<BusyContextType | undefined>(undefined);

export const BusyProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [busyCount, setBusyCount] = useState(0);
  const [busyMessage, setBusyMessage] = useState(DEFAULT_BUSY_MESSAGE);

  const startBusy = useCallback((message = DEFAULT_BUSY_MESSAGE) => {
    setBusyMessage(message);
    setBusyCount((prev) => prev + 1);
  }, []);

  const stopBusy = useCallback(() => {
    setBusyCount((prev) => {
      const nextCount = Math.max(0, prev - 1);

      if (nextCount === 0) {
        setBusyMessage(DEFAULT_BUSY_MESSAGE);
      }

      return nextCount;
    });
  }, []);

  const runBusy = useCallback(
    async <T,>(task: () => Promise<T>, message?: string): Promise<T> => {
      try {
        startBusy(message);
        return await task();
      } finally {
        stopBusy();
      }
    },
    [startBusy, stopBusy],
  );

  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const requestUrl = String(config.url || "");

        const message = requestUrl.includes("/market-analysis/analyze")
          ? MARKET_ANALYSIS_BUSY_MESSAGE
          : DEFAULT_BUSY_MESSAGE;

        startBusy(message);
        return config;
      },
      (error) => {
        stopBusy();
        return Promise.reject(error);
      },
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        stopBusy();
        return response;
      },
      (error) => {
        stopBusy();
        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [startBusy, stopBusy]);

  const value = useMemo(
    () => ({
      isBusy: busyCount > 0,
      busyMessage,
      startBusy,
      stopBusy,
      runBusy,
    }),
    [busyCount, busyMessage, startBusy, stopBusy, runBusy],
  );

  return (
    <BusyContext.Provider value={value}>
      {children}

      {busyCount > 0 && (
        <div className="app-busy-overlay">
          <div className="app-busy-box">
            <div className="app-busy-spinner"></div>
            <p>{busyMessage}</p>
          </div>
        </div>
      )}
    </BusyContext.Provider>
  );
};

export const useBusy = () => {
  const context = useContext(BusyContext);

  if (!context) {
    throw new Error("useBusy must be used inside BusyProvider");
  }

  return context;
};